import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import SelectMenu from '../ui/SelectMenu';
import { Chip } from '../ui/Chip';
import {
  UserIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ReceiptPercentIcon,
  UserGroupIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  SwatchIcon,
  DocumentTextIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { onboardingAPI, settingsAPI, customerAPI } from '../../services/api';
import { buildSettingsPayload } from './buildSettingsPayload';
import { hydrateFromSettings, dropUntouchedMargins, dropSavedBranding } from './hydrateFromSettings';
import { prepareLogo } from '../../utils/prepareLogo';
import { LOGO_ACCEPT, LOGO_MAX_BYTES, LOGO_HINT, LOGO_TOO_LARGE } from '../../utils/logoRules';
import { STATE_OPTIONS, stateFromZip } from '../ui/AddressComposite';
import RichTextEditor from '../ui/RichTextEditor';
import { useOptionalAuth } from '../../contexts/AuthContext';
import { useSettingsContext } from '../../hooks/useSettings';
import { useSettingsList } from '../../hooks/useSettingsList';
import { useResourceCategories } from '../../hooks/useResourceCategories';
import {
  INITIAL_WIZARD_DATA,
  DEFAULT_CATEGORIES,
  DEFAULT_LABOR_RATES,
} from './onboardingInitialState';
import { sanitizeTcHtml } from '../../utils/sanitizeTcHtml';
import { BASE_CUSTOMER_TYPES, typeLabel, typeSlug } from '../settings/tcCustomerTypes';
import { useToast } from '../ui/Toast';
import { formatPhoneInput } from '../../utils/phone';
import { toServiceAddressShape } from '../../utils/customerAddress';
import { humanizeEnum } from '../../utils/humanize';
import { isDeactivatedStatus } from '../../utils/userStatus';
import { NO_PAID_SEATS_MSG } from '../../utils/paidSeats';
import { ROLES } from '../settings/usersPermissions';
import {
  ColorPickerModal,
  SendInvitesModal,
  AddCrewMemberModal,
  ExampleWorkOrderModal,
  CSVMappingModal,
} from './modals';
// ─── Constants ───────────────────────────────────────────────────────────────

// IDI-738 D3: progress keeps only setup's own invitees ("inv_…" ids, Users &
// Roles' role values); a pre-change run's rows are dropped on load.
const ROLE_VALUES = ROLES.map(r => r.value);
const keepSetupInvitees = (d) => (Array.isArray(d.invitees) ? { ...d, invitees: d.invitees.filter(i =>
  i && typeof i.id === 'string' && i.id.startsWith('inv_') && ROLE_VALUES.includes(i.role)) } : d);
const newInviteeId = () => `inv_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const lc = (v) => String(v || '').trim().toLowerCase();

const STEPS = [
  { id: 1, label: 'About You', icon: UserIcon },
  { id: 2, label: 'Your Business', icon: BuildingOfficeIcon },
  { id: 3, label: 'Margins & Labor', icon: CurrencyDollarIcon },
  { id: 4, label: 'Tax & Defaults', icon: ReceiptPercentIcon },
  { id: 5, label: 'Invite Team', icon: UserGroupIcon },
  { id: 6, label: 'Crews', icon: UsersIcon },
  { id: 7, label: 'Resources', icon: WrenchScrewdriverIcon },
  { id: 8, label: 'Customers', icon: DocumentArrowUpIcon },
  { id: 9, label: "You're Set Up", icon: CheckCircleIcon },
];



// ─── Main Wizard Component ──────────────────────────────────────────────────

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [skippedSteps, setSkippedSteps] = useState(new Set());

  // IDI-659 D2: a finished dealer never reopens setup — /onboarding opens
  // the Dashboard. No just-claimed bypass (D1).
  useEffect(() => {
    onboardingAPI.getState().then(state => {
      if (state && (state.completed || state.status === 'completed')) {
        navigate('/dashboard', { replace: true });
      }
    }).catch(() => { /* ignore — let wizard proceed */ });
  }, [navigate]);

  // All wizard data
  // INITIAL_WIZARD_DATA is the CANONICAL definition of "the user did not
  // fill this in." buildSettingsPayload imports the same constant and uses
  // deep-equal-against-initial to decide which keys to include in the PUT
  // body. If a field is added here, add it to onboardingInitialState.js in
  // the same shape or the payload guard will treat it as always-touched.
  const [wizardData, setWizardData] = useState(() => ({ ...INITIAL_WIZARD_DATA }));

  // Load saved progress
  useEffect(() => {
    loadProgress();
  }, []);

  // PORTICA-62 (defect 1): loadProgress/saveProgress used to call
  // onboardingAPI.getProgress()/saveProgress() -- methods that DO NOT EXIST
  // on the api client (getState/updateStep are the real ones; see
  // services/api.js). Every call threw, was caught, logged to console, and
  // silently ignored -- so the wizard genuinely never persisted anything:
  // reloading the page always restarted from step 1 with blank data, and
  // "Next" always looked like it worked (currentStep still advanced locally)
  // while nothing was saved server-side.
  //
  // The real backend (cmd/onboarding GET/PUT) stores each step's raw data
  // blob keyed by step NAME (see stepKeyName in main.go: about_yourself,
  // about_business, margins_labor, tax, field_techs, crews, resources,
  // customer_import, completion) inside settings.onboarding_steps_data, not
  // as one flat object. None of the per-step shapes share keys (step 3 sends
  // {categories, masterDefaultMargin, laborRates}, step 6 sends {crews},
  // etc.), so flattening every step's data object back together on load
  // reconstructs the original flat wizardData shape exactly.
  const loadProgress = async () => {
    try {
      const state = await onboardingAPI.getState();
      if (!state) return;
      const stepsData = state.steps_data || state.stepsData || {};
      // IDI-690: an entry-less saved Margins & Labor yields to Settings.
      // IDI-656 D6: saved branding is ignored; Branding's current values show.
      const flattened = keepSetupInvitees(dropSavedBranding(dropUntouchedMargins(Object.values(stepsData).reduce(
        (acc, stepBlob) => (stepBlob && typeof stepBlob === 'object' ? { ...acc, ...stepBlob } : acc),
        {}
      ))));
      if (Object.keys(flattened).length > 0) {
        setWizardData(prev => ({ ...prev, ...flattened }));
      }

      // IDI-397 Leg 2: hydrate wizard fields from the tenant's EXISTING settings
      // so a migrated dealer sees their real data pre-populated, not blanks.
      try {
        const existingSettings = await settingsAPI.getSettings();
        if (existingSettings && typeof existingSettings === 'object') {
          const settingsHydration = hydrateFromSettings(
            existingSettings,
            { ...INITIAL_WIZARD_DATA, ...flattened }
          );
          if (Object.keys(settingsHydration).length > 0) {
            setWizardData(prev => ({ ...prev, ...settingsHydration, ...flattened }));
          }
        }
      } catch (settingsErr) {
        console.warn('[Onboarding] Settings hydration failed (non-fatal):', settingsErr);
      }
      const savedStep = state.current_step ?? state.currentStep;
      // Backend stores the NEXT step to resume at (see updateOnboardingStep:
      // "settings.onboarding_step = step + 1"); a fresh/never-started tenant
      // has current_step 0, which must stay on step 1, not step 0.
      if (typeof savedStep === 'number' && savedStep > 0) {
        setCurrentStep(Math.min(savedStep, 9));
      }
      const savedSkipped = state.skipped_steps || state.skippedSteps;
      if (Array.isArray(savedSkipped) && savedSkipped.length > 0) {
        setSkippedSteps(new Set(savedSkipped));
      }
    } catch (err) {
      console.error('Failed to load onboarding progress:', err);
    }
  };

  const saveProgress = async (stepData) => {
    setSaving(true);
    try {
      const updatedData = { ...wizardData, ...stepData };
      setWizardData(updatedData);
      await onboardingAPI.updateStep(currentStep, stepData, false);
    } catch (err) {
      console.error('Failed to save progress:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async (stepData) => {
    await saveProgress(stepData);
    setCurrentStep(prev => Math.min(prev + 1, 9));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSkip = () => {
    setSkippedSteps(prev => new Set([...prev, currentStep]));
    setCurrentStep(prev => Math.min(prev + 1, 9));
    // PORTICA-62 (defect 1): same persistence gap as saveProgress -- a
    // skipped step must also be recorded server-side (onboarding_skipped_steps)
    // or a reload loses which steps were skipped, corrupting the Step9Complete
    // checklist. Fire-and-forget: a failure here shouldn't block navigation,
    // it only means the summary checklist may be stale on this device until
    // the next successful save.
    onboardingAPI.updateStep(currentStep, null, true).catch((err) => {
      console.error('Failed to record skipped step:', err);
    });
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      // PR-64 §4.1 (376_ + 377_ §3): onboardingAPI.complete(wizardData) used
      // to fire HERE, before the settings GET/PUT. If the GET or PUT then
      // failed we surfaced a user-visible error and stayed on step 9, but
      // the server had already been told onboarding was done. Move it to
      // AFTER the successful PUT (below, immediately before we clear the
      // localStorage flag) so the two write paths agree.
      //
      // Idempotency measured (377_ §3):
      //   • the `onboarding_completed` boolean flag is a single-row
      //     read-modify-write — flipping it twice is fine
      //   • the `onboarding_completed_at` timestamp is last-write-wins with
      //     ZERO readers app-wide (frontend + backend-go grep = 0 hits;
      //     Family B candidate in its own right — Nevado report note)

      // Fetch the tenant's CURRENT settings before we PUT. buildSettingsPayload
      // merges user-supplied fields OVER current sub-object values, so a wizard
      // that touched only `brandColor` cannot null out a migrated tenant's logo.
      // See buildSettingsPayload.js for the full rule set (IDI-62 / DEF-089).
      //
      // FAIL CLOSED (318_ §3.2): if this GET fails we cannot promise not to
      // clobber sub-objects, so we do not PUT at all. Onboarding does NOT
      // complete — the wizard stays on step 9, the user sees a real error, and
      // we can retry. The alternative (an empty currentSettings + trust the
      // per-key guards) is exactly the shape of fallback that caused the P0
      // this PR exists to fix.
      let currentSettings;
      try {
        currentSettings = (await settingsAPI.getSettings()) || {};
      } catch (readErr) {
        console.error('Failed to read current settings before onboarding save:', readErr);
        toast.error(
          'Could not read your current settings, so we did not save the wizard. Please try again.'
        );
        setSaving(false);
        return;
      }

      // IDI-657 D3: each crew / Resource Types row setup added goes through
      // Settings' Add, keeping its text id; rows already stored are skipped,
      // so a retry adds only the rest. A failure stops before complete().
      const storedIdsOf = (list) => new Set((Array.isArray(list) ? list : [])
        .filter(x => x && x.id != null).map(x => String(x.id)));
      const adds = [
        ['crews', wizardData.crewAdds, storedIdsOf(currentSettings.crews)],
        ['resource_types', wizardData.resourceAdds, storedIdsOf(currentSettings.resourceCategories)],
      ];
      try {
        for (const [listType, rows, stored] of adds) {
          for (const row of Array.isArray(rows) ? rows : []) {
            if (!row || row.id == null || stored.has(String(row.id))) continue;
            // eslint-disable-next-line no-await-in-loop
            await settingsAPI.addListItem(listType, row);
            stored.add(String(row.id));
          }
        }
      } catch (addErr) {
        console.error('Failed to add onboarding crews/resources:', addErr);
        toast.error('Setup did not save. Please try again.');
        setSaving(false);
        return;
      }

      const settingsPayload = buildSettingsPayload(wizardData, currentSettings);

      try {
        // Only PUT if we actually have something to write. An empty payload
        // (user touched nothing) is a no-op — do not call the endpoint.
        if (Object.keys(settingsPayload).length > 0) {
          await settingsAPI.updateSettings(settingsPayload);
        }
      } catch (settingsErr) {
        // The PUT failed. This is not a silent warning — the user's wizard
        // input did not persist. Stay on the step and surface it.
        console.error('Failed to persist onboarding settings:', settingsErr);
        toast.error('Setup did not save. Please try again.');
        setSaving(false);
        return;
      }

      // PR-64 §4.1: complete() fires ONLY after the settings PUT succeeded
      // above. If the GET or PUT rejected, we returned early — the server
      // is never told onboarding was done. Do not move this back above the PUT.
      try {
        await onboardingAPI.complete(wizardData);
      } catch (completeErr) {
        // Late-stage complete() failure is unusual (the PUT already worked)
        // but must not silently pretend onboarding finished.
        console.error('Failed to mark onboarding complete:', completeErr);
        toast.error('We saved your settings but could not mark onboarding complete. Please try again.');
        setSaving(false);
        return;
      }

      toast.success('You\u2019re all set up! Welcome to BIDIT.');
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
      toast.error('Something went wrong finishing setup. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="grid grid-cols-[1fr_220px] min-h-screen">
        {/* Main Canvas */}
        <div className="flex flex-col">
          {/* Header */}
          <header className="flex items-center justify-between border-b border-[color:var(--color-border)] bg-white px-8 py-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-red-600 flex items-center justify-center" style={{ borderRadius: 'var(--radius-md)' }}>
                <span className="text-white font-bold text-sm">B</span>
              </div>
              <h1 style={{ font: 'var(--text-h1)', color: 'var(--color-text-primary)', margin: 0 }}>Set Up Your Account</h1>
            </div>
            <span className="text-sm text-gray-500 uppercase tracking-wide font-medium">
              STEP {currentStep} OF 9
            </span>
          </header>

          {/* Step Content */}
          <main className="flex-1 overflow-y-auto px-8 py-8">
            <div className="max-w-2xl mx-auto">
              {currentStep === 1 && (
                <Step1AboutYou
                  data={wizardData}
                  onNext={handleNext}
                  saving={saving}
                />
              )}
              {currentStep === 2 && (
                <Step2YourBusiness
                  data={wizardData}
                  onNext={handleNext}
                  onBack={handleBack}
                  saving={saving}
                />
              )}
              {currentStep === 3 && (
                <Step3MarginsLabor
                  data={wizardData}
                  onNext={handleNext}
                  onBack={handleBack}
                  onSkip={handleSkip}
                  saving={saving}
                />
              )}
              {currentStep === 4 && (
                <Step4TaxDefaults
                  data={wizardData}
                  onNext={handleNext}
                  onBack={handleBack}
                  onSkip={handleSkip}
                  saving={saving}
                />
              )}
              {currentStep === 5 && (
                <Step5InviteTeam
                  data={wizardData}
                  onNext={handleNext}
                  onBack={handleBack}
                  onSkip={handleSkip}
                  saving={saving}
                />
              )}
              {currentStep === 6 && (
                <Step6Crews
                  data={wizardData}
                  onNext={handleNext}
                  onBack={handleBack}
                  onSkip={handleSkip}
                  saving={saving}
                />
              )}
              {currentStep === 7 && (
                <Step7Resources
                  data={wizardData}
                  onNext={handleNext}
                  onBack={handleBack}
                  onSkip={handleSkip}
                  saving={saving}
                />
              )}
              {currentStep === 8 && (
                <Step8Customers
                  data={wizardData}
                  onNext={handleNext}
                  onBack={handleBack}
                  onSkip={handleSkip}
                  onSaveProgress={saveProgress}
                  saving={saving}
                />
              )}
              {currentStep === 9 && (
                <Step9Complete
                  data={wizardData}
                  skippedSteps={skippedSteps}
                  onComplete={handleComplete}
                  saving={saving}
                />
              )}
            </div>
          </main>
        </div>

        {/* Right Sidebar - Progress */}
        <aside className="border-l border-[color:var(--color-border)] bg-white px-6 py-8 overflow-y-auto">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
            Progress
          </h2>
          <nav className="space-y-1">
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isComplete = step.id < currentStep;
              const isSkipped = skippedSteps.has(step.id);

              return (
                <button
                  key={step.id}
                  onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                  disabled={step.id > currentStep}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                    isActive
                      ? 'bg-red-50 text-red-700 font-medium'
                      : isComplete
                      ? 'text-gray-700 hover:bg-gray-50 cursor-pointer'
                      : 'text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <div className={`flex h-7 w-7 items-center justify-center rounded-full ${
                    isActive
                      ? 'bg-red-600 text-white'
                      : isComplete
                      ? isSkipped ? 'bg-gray-200 text-gray-500' : 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    {isComplete && !isSkipped ? (
                      <CheckCircleIcon className="h-4 w-4" />
                    ) : (
                      <Icon className="h-4 w-4" />
                    )}
                  </div>
                  <span className="text-sm">{step.label}</span>
                  {isSkipped && (
                    <span className="ml-auto text-xs text-gray-400">Skipped</span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Help Card */}
          <div className="mt-8 bg-gray-50 p-4" style={{ borderRadius: 'var(--radius-md)' }}>
            <p className="text-sm font-medium text-gray-900 mb-1">Need help?</p>
            <p className="text-xs text-gray-500 mb-3">
              You can always change these settings later from the Settings page.
            </p>
            <a
              href="mailto:support@bidit.app"
              className="text-xs font-medium text-red-600 hover:text-red-700"
            >
              Contact Support →
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ─── Step 1: About You ──────────────────────────────────────────────────────

// Exported for tests only (IDI-654).
export function Step1AboutYou({ data, onNext, saving }) {
  // IDI-820 D2/D3: blank names come from the login (My Profile's, else the
  // invite name split as the customer drawer does; never the email). Email is
  // always the login's. Seeded here, after load, not in the wizard's state.
  const user = useOptionalAuth()?.user;
  const fromLogin = (prev) => {
    if (!user) return prev;
    const invite = user.name && user.name !== user.email ? String(user.name).trim().split(/\s+/) : [];
    return {
      ...prev,
      firstName: prev.firstName || user.firstName || invite[0] || '',
      lastName: prev.lastName || user.lastName || invite.slice(1).join(' ') || '',
      email: user.email || prev.email || '',
    };
  };
  const [form, setForm] = useState(() => fromLogin({
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    email: data.email || '',
    phone: data.phone || '',
  }));
  // A login that arrives after mount fills the same blanks.
  useEffect(() => {
    if (user) setForm(fromLogin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, user?.firstName, user?.lastName, user?.name]);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) onNext(form);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Tell us about yourself</h2>
      <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
        Let's start with the basics. This helps us personalize your experience.
      </p>

      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            value={form.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            error={errors.firstName}
            required
          />
          <Input
            label="Last Name"
            value={form.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            error={errors.lastName}
            required
          />
        </div>

        <Input
          label="Email"
          type="email"
          value={form.email}
          disabled
          helperText="This is the email from your invitation — it can't be changed here."
        />

        <Input
          label="Phone (optional)"
          type="tel"
          value={form.phone}
          onChange={(e) => handleChange('phone', formatPhoneInput(e.target.value))}
          placeholder="(555) 123-4567"
          helperText="Used for account recovery and notifications"
        />
      </div>

      <div className="mt-8 flex justify-end">
        <Button onClick={handleSubmit} loading={saving} size="lg">
          Continue <ArrowRightIcon className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ─── Step 2: Your Business ──────────────────────────────────────────────────

// IDI-656 D1/D2: Your Business's branding in Settings → Branding's keys, and
// the keys whose value differs from what the step showed (null if none).
function brandingOf(form) {
  return {
    logo_url: form.logoUrl || '',
    logo_meta: form.logoUrl ? form.logoMeta || null : null,
    primary_color: form.brandColor,
    secondary_color: form.secondaryColor,
    text_color: form.textColor,
    logo_position: form.logoPosition,
    email_signature: form.emailSignature,
    include_logo_in_signature: !!form.includeLogoInSignature,
  };
}

function changedBranding(shown, now) {
  const out = {};
  for (const k of Object.keys(now)) {
    if (JSON.stringify(now[k]) !== JSON.stringify(shown[k])) out[k] = now[k];
  }
  if ('logo_url' in out || 'logo_meta' in out) {
    out.logo_url = now.logo_url;
    out.logo_meta = now.logo_meta;
  }
  return Object.keys(out).length > 0 ? out : null;
}

// Exported for tests only (IDI-654).
export function Step2YourBusiness({ data, onNext, onBack, saving }) {
  const [form, setForm] = useState({
    companyName: data.companyName || '',
    zip: data.address?.zip || '',
    city: data.address?.city || '',
    state: data.address?.state || '',
    street: data.address?.street || '',
    businessPhone: data.businessPhone || '',
    businessEmail: data.businessEmail || '',
    website: data.website || '',
    logoUrl: data.logoUrl || '',
    logoMeta: data.logoMeta || null,
    logoPosition: data.logoPosition || 'left',
    brandColor: data.brandColor || '#D70321',
    secondaryColor: data.secondaryColor || '#4A4A4A',
    textColor: data.textColor || '#1F1F1F',
    includeLogoInSignature: data.includeLogoInSignature || false,
    emailSignature: data.emailSignature || '',
  });
  // IDI-656 D2: the branding as the step showed it; Continue saves only changes.
  const [brandingShown] = useState(() => brandingOf(form));
  const [errors, setErrors] = useState({});
  const [showColorPicker, setShowColorPicker] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  // IDI-820 D4: a ZIP fills a blank State by the customer drawer's table; never City.
  const handleZipChange = (value) => {
    handleChange('zip', value);
    const st = value.length === 5 ? stateFromZip(value) : null;
    if (st) setForm(prev => (String(prev.state || '').trim() ? prev : { ...prev, state: st }));
  };

  // IDI-656 D1/D3: as Settings → Branding — 2 MB cap, then prepareLogo;
  // on failure nothing is kept.
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > LOGO_MAX_BYTES) {
      setErrors(prev => ({ ...prev, logo: LOGO_TOO_LARGE }));
      return;
    }
    const result = await prepareLogo(file);
    if (!result.ok) {
      setErrors(prev => ({ ...prev, logo: result.message }));
      return;
    }
    setForm(prev => ({ ...prev, logoUrl: result.dataUrl, logoMeta: result.meta }));
    setErrors(prev => ({ ...prev, logo: null }));
  };

  const removeLogo = () => setForm(prev => ({ ...prev, logoUrl: '', logoMeta: null }));

  const validate = () => {
    const newErrors = {};
    if (!form.companyName.trim()) newErrors.companyName = 'Business name is required';
    if (!form.zip.trim()) newErrors.zip = 'ZIP is required';
    if (!form.street.trim()) newErrors.street = 'Street address is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      const branding = changedBranding(brandingShown, brandingOf(form));
      onNext({
        ...form,
        address: { street: form.street, city: form.city, state: form.state, zip: form.zip },
        ...(branding ? { branding } : {}),
      });
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Tell us about your business</h2>
      <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
        Set up your company profile, branding, and preferences.
      </p>

      <div className="space-y-8">
        {/* Section A — Business Profile */}
        <section>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Business Profile</h3>
          <div className="space-y-3">
            <Input
              label="Business Name"
              value={form.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              error={errors.companyName}
              required
            />
            {/* Zip-first address pattern */}
            <div className="grid grid-cols-6 gap-3">
              <div className="col-span-2">
                <Input
                  label="ZIP"
                  value={form.zip}
                  onChange={(e) => handleZipChange(e.target.value)}
                  error={errors.zip}
                  placeholder="78701"
                  maxLength={5}
                  required
                />
              </div>
              <div className="col-span-2">
                <Input
                  label="City"
                  value={form.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="City"
                />
              </div>
              <div className="col-span-2">
                <Input
                  label="State"
                  value={form.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="ST"
                  maxLength={2}
                />
              </div>
            </div>
            <Input
              label="Street Address"
              value={form.street}
              onChange={(e) => handleChange('street', e.target.value)}
              error={errors.street}
              required
            />
            <Input
              label="Business Phone (optional)"
              type="tel"
              value={form.businessPhone}
              onChange={(e) => handleChange('businessPhone', formatPhoneInput(e.target.value))}
              placeholder="(555) 123-4567"
            />
            <Input
              label="Business Email (optional)"
              type="email"
              value={form.businessEmail}
              onChange={(e) => handleChange('businessEmail', e.target.value)}
            />
            <Input
              label="Website (optional)"
              value={form.website}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="www.yourcompany.com"
              helperText="Used in your email signature"
            />
          </div>
        </section>

        {/* Section B — Logo */}
        <section>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Logo</h3>
          {form.logoUrl ? (
            <div className="flex items-center gap-4">
              <img src={form.logoUrl} alt="Logo" className="h-16 w-16 object-contain border border-[color:var(--color-border)]" style={{ borderRadius: 'var(--radius-md)' }} />
              <Button variant="secondary" size="sm" onClick={removeLogo}>Remove</Button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-3 rounded-md border-2 border-dashed border-[color:var(--color-border-subtle)] px-4 py-6 hover:border-[color:var(--color-border)] transition-colors w-full"
            >
              <CloudArrowUpIcon className="h-8 w-8 text-gray-400" />
              <div className="text-left">
                <p className="text-sm font-medium text-gray-700">Upload logo</p>
                <p className="text-xs text-gray-500">{LOGO_HINT}</p>
              </div>
            </button>
          )}
          <input ref={fileInputRef} type="file" accept={LOGO_ACCEPT} onChange={handleLogoUpload} className="hidden" />
          {errors.logo && <p className="mt-1 text-xs text-red-600">{errors.logo}</p>}
        </section>

        {/* Section C — Branding */}
        <section>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Branding</h3>
          <div className="space-y-4">
            {/* Logo Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo Position</label>
              <div className="inline-flex card overflow-hidden">
                {['left', 'center', 'right'].map(pos => (
                  <button
                    key={pos}
                    onClick={() => handleChange('logoPosition', pos)}
                    className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                      form.logoPosition === pos
                        ? 'bg-red-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {pos}
                  </button>
                ))}
              </div>
            </div>

            {/* Header Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Header Color</label>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 rounded-md border border-[color:var(--color-border-subtle)] px-3 py-2">
                  <div className="h-6 w-6 rounded border border-[color:var(--color-border)]" style={{ backgroundColor: form.brandColor }} />
                  <input
                    type="text"
                    value={form.brandColor}
                    onChange={(e) => handleChange('brandColor', e.target.value)}
                    className="w-20 text-sm border-none p-0 focus:ring-0"
                  />
                </div>
                <Button variant="secondary" size="sm" onClick={() => setShowColorPicker(true)}>
                  <SwatchIcon className="h-4 w-4 mr-1" /> Pick color…
                </Button>
              </div>
            </div>

            {/* Secondary Color — parity with Settings → Branding */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
              <div className="flex items-center gap-2 rounded-md border border-[color:var(--color-border-subtle)] px-3 py-2 w-fit">
                <input
                  type="color"
                  value={form.secondaryColor}
                  onChange={(e) => handleChange('secondaryColor', e.target.value)}
                  className="h-6 w-6 rounded border border-[color:var(--color-border)] cursor-pointer p-0"
                  aria-label="Secondary color"
                />
                <input
                  type="text"
                  value={form.secondaryColor}
                  onChange={(e) => handleChange('secondaryColor', e.target.value)}
                  className="w-20 text-sm border-none p-0 focus:ring-0"
                />
              </div>
            </div>

            {/* Text / Font Color — parity with Settings → Branding */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Text / Font Color</label>
              <div className="flex items-center gap-2 rounded-md border border-[color:var(--color-border-subtle)] px-3 py-2 w-fit">
                <input
                  type="color"
                  value={form.textColor}
                  onChange={(e) => handleChange('textColor', e.target.value)}
                  className="h-6 w-6 rounded border border-[color:var(--color-border)] cursor-pointer p-0"
                  aria-label="Text/font color"
                />
                <input
                  type="text"
                  value={form.textColor}
                  onChange={(e) => handleChange('textColor', e.target.value)}
                  className="w-20 text-sm border-none p-0 focus:ring-0"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Applied to document body text on quotes & invoices.</p>
            </div>

            {/* Include logo in signature */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.includeLogoInSignature}
                onChange={(e) => handleChange('includeLogoInSignature', e.target.checked)}
                className="h-4 w-4 rounded border-[color:var(--color-border)] text-red-600"
              style={{ accentColor: 'var(--color-action-primary)' }}
              />
              <span className="text-sm text-gray-700">Include my logo in the email signature</span>
            </label>

            {/* Sample Quote Preview */}
            <div className="card p-4 bg-gray-50">
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">Sample Quote Preview</p>
              <div className="bg-white rounded p-4">
                <div className={`flex items-center border-b-2 pb-3 mb-3 ${
                  form.logoPosition === 'center' ? 'justify-center' : form.logoPosition === 'right' ? 'justify-end' : 'justify-start'
                }`} style={{ borderColor: form.brandColor }}>
                  {form.logoUrl ? (
                    <img src={form.logoUrl} alt="" className="h-8 object-contain" />
                  ) : (
                    <div className="h-8 w-24 rounded bg-gray-200 flex items-center justify-center text-xs text-gray-400">Logo</div>
                  )}
                </div>
                <div className="space-y-1">
                  <div className="h-2 w-3/4 bg-gray-100 rounded" />
                  <div className="h-2 w-1/2 bg-gray-100 rounded" />
                  <div className="h-2 w-2/3 bg-gray-100 rounded" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section E — Email Signature */}
        <section>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Email Signature</h3>
          {/* Editable signature text — parity with Settings → Branding */}
          <textarea
            value={form.emailSignature}
            onChange={(e) => handleChange('emailSignature', e.target.value)}
            rows={3}
            placeholder={`${data.firstName || ''} ${data.lastName || ''}\n${form.companyName || 'Your Company'}`}
            className="w-full rounded-md border border-[color:var(--color-border-subtle)] px-3 py-2 text-sm focus:ring-red-500 focus:border-red-500 mb-3"
          />
          {/* Live preview — inherits logo toggle + text color, same as Settings */}
          <div className="card p-4">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Preview</p>
            <div className="flex items-start gap-3">
              {form.includeLogoInSignature && form.logoUrl && (
                <img src={form.logoUrl} alt="" className="h-10 w-10 object-contain rounded" />
              )}
              <div className="text-sm" style={{ color: form.textColor }}>
                {form.emailSignature ? (
                  <p className="whitespace-pre-line">{form.emailSignature}</p>
                ) : (
                  <>
                    <p className="font-medium">{data.firstName} {data.lastName}</p>
                    <p>{form.companyName || 'Your Company'}</p>
                    <p>{data.phone || ''}</p>
                    <p>{data.email || ''}</p>
                    {form.website && (
                      <p style={{ color: form.brandColor }}>{form.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">This signature is attached to quotes and invoices sent via email.</p>
        </section>
      </div>

      <div className="mt-8 flex justify-between">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" /> Back
        </Button>
        <Button onClick={handleSubmit} loading={saving} size="lg">
          Continue <ArrowRightIcon className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* Color Picker Modal */}
      <ColorPickerModal
        open={showColorPicker}
        onClose={() => setShowColorPicker(false)}
        onApply={(color) => handleChange('brandColor', color)}
        value={form.brandColor}
        title="Choose Header Color"
      />
    </div>
  );
}

// ─── Step 3: Margins & Labor ────────────────────────────────────────────────

// Exported for tests only (Step3.rendered.test.js) — the component is
// otherwise consumed only via <OnboardingWizard/> below.
export function Step3MarginsLabor({ data, onNext, onBack, onSkip, saving }) {
  // DEFAULT_CATEGORIES and DEFAULT_LABOR_RATES are imported from
  // onboardingInitialState.js — the canonical initial state (320_ §2).
  // With the import in place, `data.categories || DEFAULT_CATEGORIES` is
  // inert: INITIAL_WIZARD_DATA already carries the same reference-equal
  // DEFAULT_CATEGORIES, so a no-change Next commits the same value that was
  // already in wizardData and touched() stays false.
  //
  // §1 (2026-08-07): labor rates shape MUST equal Settings → Margins & Labor
  // ({ piece, hourly, day }) so the wizard's write and Settings' read land
  // at the same key.

  const [categories, setCategories] = useState(data.categories || DEFAULT_CATEGORIES);
  const [masterDefaultMargin, setMasterDefaultMargin] = useState(data.masterDefaultMargin ?? 5);
  // §1: normalise legacy array shape to the flat object shape if we hydrate
  // from a prior wizard save. Nothing wrote the array shape to Settings in a
  // usable form, so discarding it is not data loss.
  const normaliseLaborRates = (input) => {
    if (input && typeof input === 'object' && !Array.isArray(input)) return input;
    return DEFAULT_LABOR_RATES;
  };
  const [laborRates, setLaborRates] = useState(normaliseLaborRates(data.laborRates));
  const [marginError, setMarginError] = useState('');

  // IDI-397 D4: any value may be typed; Continue checks 0–99 (decimals ok).
  const handleCategoryMarginChange = (id, margin) => {
    const val = margin === '' ? null : parseFloat(margin);
    if (val === null || !isNaN(val)) {
      setCategories(prev => prev.map(c => c.id === id ? { ...c, margin: val } : c));
    }
  };

  const handleLaborRateChange = (field, value) => {
    if (field === "sq_ft" || field === "linear_ft") {
      setLaborRates(prev => ({ ...prev, piece: { ...prev.piece, [field]: value } }));
    } else {
      setLaborRates(prev => ({ ...prev, [field]: value }));
    }
  };

  const inRange = (v) => v !== '' && v != null && Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 99;
  const handleSubmit = () => {
    const ok = inRange(masterDefaultMargin) && categories.every(c => c.margin == null || c.margin === '' || inRange(c.margin));
    if (!ok) {
      setMarginError('Enter a margin from 0% to 99%.');
      return;
    }
    setMarginError('');
    onNext({ categories, masterDefaultMargin, laborRates });
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Set your margin and labor defaults</h2>
      <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
        Configure profit margins per item category and your company's default labor rates.
      </p>

      {/* Section A — Margin Defaults by Item Category */}
      <section className="mb-8">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Margin Defaults by Item Category</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {categories.map((cat) => (
            <div key={cat.id} className="card p-4 flex flex-col items-start relative">
              <span className="text-sm font-medium text-gray-900 mb-2">{cat.name}</span>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">Margin:</span>
                <input
                  type="number"
                  min="0"
                  max="99"
                  aria-label={`${cat.name} margin`}
                  value={cat.margin ?? ''}
                  onChange={(e) => handleCategoryMarginChange(cat.id, e.target.value)}
                  className="w-14 rounded border border-[color:var(--color-border)] px-2 py-1 text-sm text-right"
                />
                <span className="text-xs text-gray-500">%</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section B — Labor */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Labor</h3>

        {/* §30.5: Default Margin — lowest-tier fallback */}
        <div className="card mb-4 px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Default Margin</p>
              <p className="text-xs text-gray-500">Applied when no category or customer margin is set</p>
            </div>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0"
                max="99"
                aria-label="Default Margin"
                value={masterDefaultMargin}
                onChange={(e) => setMasterDefaultMargin(e.target.value)}
                className="w-16 rounded border border-[color:var(--color-border)] px-2 py-1 text-sm text-right"
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
          </div>
        </div>

        {/* §1 (2026-08-07): flat 4-rate form matching Settings → Margins & Labor.
             Was: card-based array with per-area "Applies to" chips + "+ Add labor rate".
             That model was abandoned; Settings uses 4 flat company-wide rates. */}
        <div className="space-y-3 card p-4">
          <div className="flex items-center justify-between">
            <label htmlFor="lr-sqft" className="text-sm text-gray-700">Per Square Foot</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">$</span>
              <input
                id="lr-sqft"
                type="number" min="0" step="0.01"
                value={laborRates.piece?.sq_ft || ''}
                onChange={(e) => handleLaborRateChange('sq_ft', e.target.value)}
                className="w-24 rounded border border-[color:var(--color-border)] px-2 py-1 text-sm text-right"
                placeholder="0.00"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label htmlFor="lr-lft" className="text-sm text-gray-700">Per Linear Foot</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">$</span>
              <input
                id="lr-lft"
                type="number" min="0" step="0.01"
                value={laborRates.piece?.linear_ft || ''}
                onChange={(e) => handleLaborRateChange('linear_ft', e.target.value)}
                className="w-24 rounded border border-[color:var(--color-border)] px-2 py-1 text-sm text-right"
                placeholder="0.00"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label htmlFor="lr-hourly" className="text-sm text-gray-700">Hourly</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">$</span>
              <input
                id="lr-hourly"
                type="number" min="0" step="0.01"
                value={laborRates.hourly}
                onChange={(e) => handleLaborRateChange('hourly', e.target.value)}
                className="w-24 rounded border border-[color:var(--color-border)] px-2 py-1 text-sm text-right"
                placeholder="0.00"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label htmlFor="lr-day" className="text-sm text-gray-700">Day</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">$</span>
              <input
                id="lr-day"
                type="number" min="0" step="0.01"
                value={laborRates.day}
                onChange={(e) => handleLaborRateChange('day', e.target.value)}
                className="w-24 rounded border border-[color:var(--color-border)] px-2 py-1 text-sm text-right"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>
      </section>

      {marginError && (
        <p role="alert" className="mt-4 text-sm text-red-600">{marginError}</p>
      )}

      <div className="mt-8 flex justify-between">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={onSkip}>Skip for now</Button>
          <Button onClick={handleSubmit} loading={saving} size="lg">
            Continue <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 4: Tax & Defaults ─────────────────────────────────────────────────

// IDI-626: a T&C Templates entry as setup's list and editor see it.
const tcView = (t) => ({ id: t.id, name: t.name ?? t.title ?? '', content: t.content || '',
  customer_types: Array.isArray(t.customer_types) ? t.customer_types : [] });

export function Step4TaxDefaults({ data, onNext, onBack, onSkip, saving }) {
  // IDI-626 D3/D7: Settings' T&C Templates entries with setup's changes over
  // them; only the changes are progress.
  const storedTc = (Array.isArray(data.termsAndConditions) ? data.termsAndConditions : [])
    .filter(t => t && t.id != null).map(tcView);
  const [tcChanges, setTcChanges] = useState(() => {
    const p = data.tcChanges || {};
    return { added: Array.isArray(p.added) ? p.added : [], edits: p.edits || {}, deleted: Array.isArray(p.deleted) ? p.deleted : [] };
  });
  const storedIds = new Set(storedTc.map(t => t.id));
  const tcList = [
    ...storedTc.filter(t => !tcChanges.deleted.includes(t.id)).map(t => ({ ...t, ...(tcChanges.edits[t.id] || {}) })),
    ...tcChanges.added.filter(t => !storedIds.has(t.id)),
  ];
  // IDI-666: Quote Defaults' own terms (D2), pre-filled from its stored term,
  // else Net 30 (D3); an earlier run's pick shows over it (D5).
  const { getOptions, quoteDefaults } = useSettingsContext();
  const termOptions = getOptions('payment_terms');
  const storedTerm = quoteDefaults?.payment_terms || data.storedPaymentTerm || 'net_30';
  const [termPick, setTermPick] = useState(data.paymentTermPick || null);
  const selectedTerm = termPick || storedTerm;
  const [showTCEditor, setShowTCEditor] = useState(false);
  const [editingTC, setEditingTC] = useState(null);
  // IDI-655: Sales Tax by State. Stored rows (with a state) edit only their
  // rate; added rows pick a state. Only typed changes go to step progress.
  const stored = (Array.isArray(data.storedTaxRates) ? data.storedTaxRates : [])
    .filter(r => r && r.id != null && String(r.state || '').trim());
  const prior = data.taxRateEdits || {};
  const [storedRates, setStoredRates] = useState(() => Object.fromEntries(stored.map(r => [r.id,
    String((prior.edits && prior.edits[r.id] != null) ? prior.edits[r.id] : (r.rate ?? ''))])));
  const [addedRows, setAddedRows] = useState(() => {
    const prev = (Array.isArray(prior.added) ? prior.added : [])
      .map((a, i) => ({ key: i, state: a.state || '', rate: String(a.rate ?? '') }));
    if (prev.length || stored.length) return prev;
    const home = String(data.address?.state || '').trim().toUpperCase();
    return [{ key: 0, state: STATE_OPTIONS.some(o => o.value === home) ? home : '', rate: '' }];
  });
  const [taxError, setTaxError] = useState('');
  const setAdded = (key, field, value) =>
    setAddedRows(rows => rows.map(r => (r.key === key ? { ...r, [field]: value } : r)));
  const badRate = (v) => { const n = Number(v); return String(v).trim() === '' || !Number.isFinite(n) || n < 0 || n > 100; };

  const handleSubmit = () => {
    const filled = addedRows.filter(r => r.state || String(r.rate).trim() !== '');
    const seen = new Set(stored.map(r => String(r.state).trim().toUpperCase()));
    let err = '';
    if (filled.some(r => !r.state && String(r.rate).trim() !== '')) err = 'Choose a state.';
    for (const r of filled) {
      if (err) break;
      if (r.state && seen.has(r.state)) err = `${r.state} already has a tax rate.`;
      seen.add(r.state);
    }
    if (!err && (stored.some(r => badRate(storedRates[r.id]))
      || filled.some(r => String(r.rate).trim() !== '' && badRate(r.rate)))) {
      err = 'Enter a rate from 0% to 100%.';
    }
    setTaxError(err);
    if (err) return;
    const edits = {};
    stored.forEach((r) => {
      if (Number(storedRates[r.id]) !== Number(r.rate)) edits[r.id] = Number(storedRates[r.id]);
    });
    const added = filled.filter(r => r.state && String(r.rate).trim() !== '')
      .map(r => ({ state: r.state, rate: Number(r.rate) }));
    // IDI-666 D4: only a term other than the stored one is progress.
    const paymentTermPick = selectedTerm !== storedTerm ? selectedTerm : undefined;
    onNext({ paymentTermPick, taxRateEdits: { edits, added }, tcChanges });
  };

  // IDI-626 D1: a new document gets its text id now and keeps it.
  const handleAddTC = () => {
    const ids = new Set(tcList.map(t => t.id));
    let n = Date.now();
    while (ids.has(`tc_${n}`)) n += 1;
    setEditingTC({ id: `tc_${n}`, name: '', content: '', customer_types: [], isNew: true });
    setShowTCEditor(true);
  };

  const handleSaveTC = ({ id, name, content, customer_types }) => {
    const doc = { id, name, content, customer_types };
    setTcChanges((c) => {
      if (!storedIds.has(id)) {
        const has = c.added.some(t => t.id === id);
        return { ...c, added: has ? c.added.map(t => (t.id === id ? doc : t)) : [...c.added, doc] };
      }
      const edits = { ...c.edits };
      delete edits[id];
      const orig = storedTc.find(t => t.id === id);
      // IDI-820 D1: the editor cleans stored HTML on open; cleaning alone is no change.
      const same = orig.name === name && sanitizeTcHtml(orig.content) === sanitizeTcHtml(content)
        && JSON.stringify(orig.customer_types) === JSON.stringify(customer_types);
      return { ...c, edits: same ? edits : { ...edits, [id]: { name, content, customer_types } } };
    });
    setShowTCEditor(false);
    setEditingTC(null);
  };

  const handleDeleteTC = (id) => {
    setTcChanges((c) => {
      const edits = { ...c.edits };
      delete edits[id];
      return storedIds.has(id)
        ? { ...c, edits, deleted: [...c.deleted, id] }
        : { ...c, added: c.added.filter(t => t.id !== id) };
    });
  };


  return (
    <div>
      <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Tax & Defaults</h2>
      <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
        Configure sales tax rates and default terms & conditions for your quotes and invoices.
      </p>

      <div className="space-y-6">
        {/* IDI-655: Sales Tax by State */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sales Tax by State</label>
          <div className="grid grid-cols-[1fr_8rem_2rem] gap-2 items-center text-sm">
            <span className="text-xs font-medium text-gray-500">State</span>
            <span className="text-xs font-medium text-gray-500">Rate %</span>
            <span />
            {stored.map((r) => (
              <React.Fragment key={`s-${r.id}`}>
                <span>{STATE_OPTIONS.find(o => o.value === String(r.state).trim().toUpperCase())?.label || r.state}</span>
                <input type="number" min="0" max="100" step="any" aria-label={`Rate % for ${r.state}`}
                  value={storedRates[r.id]} className="w-24 rounded border border-[color:var(--color-border)] px-2 py-1.5 text-sm text-right"
                  onChange={(e) => { const v = e.target.value; setStoredRates(p => ({ ...p, [r.id]: v })); }} />
                <span />
              </React.Fragment>
            ))}
            {addedRows.map((r) => (
              <React.Fragment key={`a-${r.key}`}>
                <Select aria-label="State" value={r.state} onChange={(e) => setAdded(r.key, 'state', e.target.value)}>
                  <option value="">—</option>
                  {STATE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
                <input type="number" min="0" max="100" step="any" aria-label="Rate %"
                  value={r.rate} className="w-24 rounded border border-[color:var(--color-border)] px-2 py-1.5 text-sm text-right"
                  onChange={(e) => setAdded(r.key, 'rate', e.target.value)} />
                <button type="button" aria-label="Remove state" className="text-gray-400 hover:text-red-500"
                  onClick={() => setAddedRows(rows => rows.filter(x => x.key !== r.key))}>×</button>
              </React.Fragment>
            ))}
          </div>
          <Button variant="ghost" size="sm" className="mt-2"
            onClick={() => setAddedRows(rows => [...rows, { key: Math.max(-1, ...rows.map(x => x.key)) + 1, state: '', rate: '' }])}>
            + Add a state
          </Button>
          {taxError && <p className="mt-1 text-sm text-red-600" role="alert">{taxError}</p>}
        </div>

        {/* Payment Terms */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Default Payment Terms</label>
          <p className="text-xs text-gray-500 mb-3">Applied to new invoices by default.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {termOptions.map((term) => (
              <button
                key={term.value}
                type="button"
                aria-pressed={selectedTerm === term.value}
                onClick={() => setTermPick(term.value)}
                className={`rounded-md border border-[color:var(--color-border)] px-3 py-2.5 text-sm font-medium transition-colors ${
                  selectedTerm === term.value
                    ? 'bg-red-50 border-red-300 text-red-700'
                    : 'bg-white border-[color:var(--color-border)] text-gray-700 hover:border-[color:var(--color-border)]'
                }`}
              >
                {term.label}
              </button>
            ))}
          </div>
        </div>

        {/* Terms & Conditions */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Terms & Conditions</label>
              <p className="text-xs text-gray-500">Attached to quotes and invoices</p>
            </div>
            <Button variant="secondary" size="sm" onClick={handleAddTC}>
              <PlusIcon className="h-4 w-4 mr-1" /> Add Document
            </Button>
          </div>

          {tcList.length === 0 ? (
            <div className="rounded-md border-2 border-dashed border-[color:var(--color-border)] p-6 text-center">
              <DocumentTextIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No terms & conditions yet</p>
              <p className="text-xs text-gray-400 mt-1">Add documents that will be attached to your quotes</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tcList.map((tc) => (
                <div key={tc.id} className="flex items-center justify-between card px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{tc.name}</p>
                    <p className="text-xs text-gray-500">
                      {tc.customer_types.length > 0
                        ? `Applies to: ${tc.customer_types.map(typeLabel).join(', ')}`
                        : 'Applies to all customers'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setEditingTC(tc); setShowTCEditor(true); }}
                    >
                      Edit
                    </Button>
                    <button
                      onClick={() => handleDeleteTC(tc.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 flex justify-between">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={onSkip}>Skip for now</Button>
          <Button onClick={handleSubmit} loading={saving} size="lg">
            Continue <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
      {/* T&C Editor Modal */}
      {showTCEditor && (
        <TCEditorModal
          open={showTCEditor}
          onClose={() => { setShowTCEditor(false); setEditingTC(null); }}
          onSave={handleSaveTC}
          initialData={editingTC}
        />
      )}
    </div>
  );
}

// T&C Editor Modal (inline)
// IDI-626 D4/D5: T&C Templates' chips and custom types; no attachment.
function TCEditorModal({ open, onClose, onSave, initialData }) {
  const [title, setTitle] = useState(initialData?.name || '');
  const [content, setContent] = useState(initialData?.content || '');
  const [customerTypes, setCustomerTypes] = useState(initialData?.customer_types || []);
  const [customTypes, setCustomTypes] = useState([]);
  const [customInput, setCustomInput] = useState('');
  const typeOptions = [...new Set([...BASE_CUSTOMER_TYPES.map(o => o.value), ...customTypes, ...customerTypes])];
  const toggleType = (v) => setCustomerTypes(prev => (prev.includes(v) ? prev.filter(t => t !== v) : [...prev, v]));
  const addCustomType = () => {
    const v = typeSlug(customInput);
    if (!v) return;
    if (!typeOptions.includes(v)) setCustomTypes(prev => [...prev, v]);
    if (!customerTypes.includes(v)) setCustomerTypes(prev => [...prev, v]);
    setCustomInput('');
  };

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({ id: initialData?.id, name: title.trim(), content, customer_types: customerTypes });
  };

  return (
    <div className={`fixed inset-0 z-50 ${open ? '' : 'hidden'}`}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute inset-4 bg-white flex flex-col" style={{ borderRadius: 'var(--radius-md)' }}>
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {initialData?.isNew ? 'New Terms & Conditions' : 'Edit Document'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <Input
            label="Document Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Standard Terms & Conditions"
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            {/* IDI-820 S1: Settings → T&C Templates' editor, as it is. */}
            <RichTextEditor
              key={`tc-rte-${initialData?.id ?? 'new'}`}
              value={content}
              onChange={setContent}
              placeholder="Enter your terms and conditions..."
              minHeight={220}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Apply to Customer Types (optional)
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Leave empty to apply to all customers, or select specific types.
            </p>
            <div className="flex flex-wrap gap-2">
              {typeOptions.map(type => (
                <button
                  key={type}
                  type="button"
                  aria-pressed={customerTypes.includes(type)}
                  onClick={() => toggleType(type)}
                  className={`rounded-full px-3 py-1 text-sm border transition-colors ${
                    customerTypes.includes(type)
                      ? 'bg-red-50 border-red-300 text-red-700'
                      : 'bg-white border-[color:var(--color-border)] text-gray-600 hover:border-[color:var(--color-border)]'
                  }`}
                >
                  {typeLabel(type)}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomType(); } }}
                placeholder="+ Add custom type"
                className="flex-1"
              />
              <Button type="button" variant="secondary" onClick={addCustomType} disabled={!customInput.trim()}>
                Add
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4">
          <div className="flex items-center justify-end gap-3">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={!title.trim()}>Save Document</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 5: Invite Your Team ───────────────────────────────────────────────

// IDI-737 D1: Users & Roles' ROLES carry no seat; each role's seat for add_user.
const ROLE_SEAT = { admin: 'paid', salesperson: 'paid', field_tech: 'viewer' };

function Step5InviteTeam({ data, onNext, onBack, onSkip, saving }) {
  const [adminInvitees, setAdminInvitees] = useState(
    (data.invitees || []).filter(i => i.role !== 'field_tech')
  );
  const [fieldTechInvitees, setFieldTechInvitees] = useState(
    (data.invitees || []).filter(i => i.role === 'field_tech')
  );
  const [newAdmin, setNewAdmin] = useState({ firstName: '', lastName: '', email: '', role: 'admin' });
  const [newFieldTech, setNewFieldTech] = useState({ firstName: '', email: '' });
  const [errors, setErrors] = useState({});
  const [showSendInvites, setShowSendInvites] = useState(false);
  // IDI-738 D2: Users & Roles' list, read when the step opens; a failed read shows no list.
  const [teamUsers, setTeamUsers] = useState([]);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const users = await settingsAPI.getTenantUsers?.();
        if (live && Array.isArray(users)) setTeamUsers(users.filter(Boolean));
      } catch (err) { console.warn('[Onboarding] Team read failed (non-fatal):', err); }
    })();
    return () => { live = false; };
  }, []);
  // D4: anyone on the team (any status) is never listed again as an invitee.
  const teamEmails = new Set(teamUsers.map(u => lc(u.email)).filter(Boolean));
  const visibleTeam = teamUsers.filter(u => !isDeactivatedStatus(u.status));
  const notOnTeam = (i) => !teamEmails.has(lc(i.email));
  const pendingAdmins = adminInvitees.filter(notOnTeam);
  const pendingFieldTechs = fieldTechInvitees.filter(notOnTeam);
  const allInvitees = [...pendingAdmins, ...pendingFieldTechs];
  const isTaken = (email) => teamEmails.has(lc(email)) || allInvitees.some(i => lc(i.email) === lc(email));

  // Admin/Salesperson — individual card add
  const handleAddAdmin = () => {
    const newErrors = {};
    if (!newAdmin.firstName.trim()) newErrors.adminFirst = 'Required';
    if (!newAdmin.email.trim()) newErrors.adminEmail = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newAdmin.email)) newErrors.adminEmail = 'Invalid email';
    else if (isTaken(newAdmin.email)) newErrors.adminEmail = 'Already added';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setAdminInvitees(prev => [...prev, { ...newAdmin, id: newInviteeId() }]);
    setNewAdmin({ firstName: '', lastName: '', email: '', role: 'admin' });
    setErrors({});
  };

  // Field Tech — bulk invite (name + email only)
  const handleAddFieldTech = () => {
    const newErrors = {};
    if (!newFieldTech.firstName.trim()) newErrors.ftFirst = 'Required';
    if (!newFieldTech.email.trim()) newErrors.ftEmail = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newFieldTech.email)) newErrors.ftEmail = 'Invalid email';
    else if (isTaken(newFieldTech.email)) newErrors.ftEmail = 'Already added';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setFieldTechInvitees(prev => [...prev, { ...newFieldTech, role: 'field_tech', id: newInviteeId() }]);
    setNewFieldTech({ firstName: '', email: '' });
    setErrors({});
  };

  const handleRemove = (id, type) => {
    if (type === 'admin') setAdminInvitees(prev => prev.filter(i => i.id !== id));
    else setFieldTechInvitees(prev => prev.filter(i => i.id !== id));
  };

  // IDI-737 D1/D2/D4: one add_user per pending person, in order; a failure stays
  // on that person. Passwords go back to the dialog only, never saved.
  const handleSendInvites = async (inviteeList) => {
    const results = [];
    const created = [];
    for (const person of inviteeList) {
      const name = `${(person.firstName || '').trim()} ${(person.lastName || '').trim()}`.trim();
      try {
        const res = await settingsAPI.addUser({
          name, email: person.email, role: person.role, seat_type: ROLE_SEAT[person.role] || 'viewer',
        });
        results.push({ person, ok: true, temporaryPassword: res?.temporaryPassword,
          emailStatus: res?.emailStatus, email: res?.user?.email || person.email });
        const u = res?.user || {};
        created.push({ ...u, name: u.name || name, email: u.email || person.email,
          role: u.role || person.role, status: u.status || 'active' });
      } catch (err) {
        const status = err?.response?.status ?? err?.status;
        // IDI-226 (2): a 422 is the dealer's paid seats all in use.
        results.push({ person, ok: false, error: status === 409
          ? 'A user with this email already exists'
          : status === 422 ? NO_PAID_SEATS_MSG
            : "Couldn't create the user. Try again." });
      }
    }
    if (created.length) setTeamUsers(prev => [...prev, ...created]);
    return results;
  };

  // IDI-737 D3: Continue saves everyone setup listed, sent or not.
  const handleSubmit = () => onNext({ invitees: [...adminInvitees, ...fieldTechInvitees] });

  return (
    <div>
      <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Invite Your Team</h2>
      <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
        Add team members who need access to BIDIT.
      </p>

      {/* Already on your team — Users & Roles' active users, read-only (IDI-738 D2) */}
      {visibleTeam.length > 0 && (
        <section className="mb-8" aria-label="Already on your team">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Already on your team</h3>
          <div className="card divide-y divide-[color:var(--color-border-subtle)]">
            {visibleTeam.map(u => (
              <div key={u.id || u.email} className="flex items-center justify-between px-4 py-2.5">
                <div>
                  <p className="text-sm font-medium text-gray-900">{u.name || u.email}</p>
                  <p className="text-xs text-gray-500">{u.email}</p>
                </div>
                <Chip>{humanizeEnum(u.role || 'salesperson')}</Chip>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Section A — Admin / Salesperson (individual card) */}
      <section className="mb-8">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Admins & Salespeople</h3>
        <div className="card p-4 mb-3">
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-3">
              <input placeholder="First name" value={newAdmin.firstName}
                onChange={(e) => setNewAdmin(p => ({ ...p, firstName: e.target.value }))}
                className={`input text-sm ${errors.adminFirst ? 'input-error' : ''}`} />
            </div>
            <div className="col-span-2">
              <input placeholder="Last name" value={newAdmin.lastName}
                onChange={(e) => setNewAdmin(p => ({ ...p, lastName: e.target.value }))}
                className="input text-sm" />
            </div>
            <div className="col-span-3">
              <input placeholder="Email" type="email" value={newAdmin.email}
                onChange={(e) => setNewAdmin(p => ({ ...p, email: e.target.value }))}
                className={`input text-sm ${errors.adminEmail ? 'input-error' : ''}`} />
            </div>
            <div className="col-span-3">
              <SelectMenu
                value={newAdmin.role}
                onChange={(val) => setNewAdmin(p => ({ ...p, role: val }))}
                options={ROLES.filter(r => r.value !== 'field_tech').map(r => ({ value: r.value, label: r.label }))}
                className="text-sm"
              />
            </div>
            <div className="col-span-1">
              <Button variant="secondary" size="sm" onClick={handleAddAdmin} className="w-full">
                <PlusIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {(errors.adminFirst || errors.adminEmail) && (
            <p className="text-xs text-red-600 mt-1">{errors.adminFirst || errors.adminEmail}</p>
          )}
        </div>
        {pendingAdmins.length > 0 && (
          <div className="card divide-y divide-[color:var(--color-border-subtle)]">
            {pendingAdmins.map(person => (
              <div key={person.id} className="flex items-center justify-between px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-red-100 flex items-center justify-center text-xs font-medium text-red-700">
                    {person.firstName[0]}{person.lastName?.[0] || ''}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{person.firstName} {person.lastName}</p>
                    <p className="text-xs text-gray-500">{person.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Chip>{person.role === 'salesperson' ? 'Salesperson' : 'Admin'}</Chip>
                  <button onClick={() => handleRemove(person.id, 'admin')} className="text-gray-400 hover:text-red-500">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Section B — Field Techs (bulk invite — name + email only) */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Field Techs</h3>
        <p className="text-xs text-gray-500 mb-3">Field techs get a simplified mobile view. Only name and email are needed.</p>
        <p className="text-xs text-green-600 mb-3 font-medium">Field tech seats are free and unlimited.</p>
        <div className="card p-4 mb-3">
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-5">
              <input placeholder="Name" value={newFieldTech.firstName}
                onChange={(e) => setNewFieldTech(p => ({ ...p, firstName: e.target.value }))}
                className={`input text-sm ${errors.ftFirst ? 'input-error' : ''}`} />
            </div>
            <div className="col-span-5">
              <input placeholder="Email" type="email" value={newFieldTech.email}
                onChange={(e) => setNewFieldTech(p => ({ ...p, email: e.target.value }))}
                className={`input text-sm ${errors.ftEmail ? 'input-error' : ''}`} />
            </div>
            <div className="col-span-2">
              <Button variant="secondary" size="sm" onClick={handleAddFieldTech} className="w-full">
                <PlusIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {(errors.ftFirst || errors.ftEmail) && (
            <p className="text-xs text-red-600 mt-1">{errors.ftFirst || errors.ftEmail}</p>
          )}
        </div>
        {pendingFieldTechs.length > 0 && (
          <div className="card divide-y divide-[color:var(--color-border-subtle)]">
            {pendingFieldTechs.map(person => (
              <div key={person.id} className="flex items-center justify-between px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center text-xs font-medium text-green-700">
                    {person.firstName[0]}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{person.firstName}</p>
                    <p className="text-xs text-gray-500">{person.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Chip color="success">Field Tech</Chip>
                  <button onClick={() => handleRemove(person.id, 'field_tech')} className="text-gray-400 hover:text-red-500">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Send Invites */}
      {allInvitees.length > 0 && (
        <div className="mb-4">
          <Button variant="secondary" onClick={() => setShowSendInvites(true)} className="w-full">
            Send {allInvitees.length} Invitation{allInvitees.length !== 1 ? 's' : ''} Now
          </Button>
          <p className="text-xs text-gray-500 text-center mt-1">Or continue — you can add people later in Settings → Users & Roles.</p>
        </div>
      )}

      <div className="mt-8 flex justify-between">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={onSkip}>Skip for now</Button>
          <Button onClick={handleSubmit} loading={saving} size="lg">
            Continue <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      <SendInvitesModal open={showSendInvites} onClose={() => setShowSendInvites(false)}
        onConfirm={handleSendInvites} invitees={allInvitees} />
    </div>
  );
}

// ─── Step 6: Crews ──────────────────────────────────────────────────────────

// IDI-657: a text id given when a crew or Resource Types row is added; it is
// kept when the row is written through Settings' add_list_item.
const newTextId = (prefix, taken) => {
  let n = Date.now();
  while (taken.has(`${prefix}_${n}`)) n += 1;
  return `${prefix}_${n}`;
};

// IDI-657 D1/D4: how many people a crew lists (logins, invites, placeholders).
const crewPeople = (c) => ['members', 'pending_invites', 'placeholders']
  .reduce((n, k) => n + (Array.isArray(c && c[k]) ? c[k].length : 0), 0);

// Exported for tests only (IDI-657).
function Step6Crews({ data, onNext, onBack, onSkip, saving }) {
  // IDI-657 D4: Settings' crews show read-only; setup's adds (D1 shape) are
  // the step's only progress. A replayed add already stored shows once (D5).
  const storedCrews = (Array.isArray(data.storedCrews) ? data.storedCrews : []).filter(c => c && c.id != null);
  const storedIds = new Set(storedCrews.map(c => String(c.id)));
  const [crewAdds, setCrewAdds] = useState(() => (Array.isArray(data.crewAdds) ? data.crewAdds : [])
    .filter(c => c && !storedIds.has(String(c.id))));
  const [showAddMember, setShowAddMember] = useState(false);
  const [activeCrewId, setActiveCrewId] = useState(null);
  const [colorCrewId, setColorCrewId] = useState(null);
  const [newCrewName, setNewCrewName] = useState('');
  const [newCrewDescription, setNewCrewDescription] = useState('');
  const [newCrewColor, setNewCrewColor] = useState('#D70321');

  // 20 preset crew colors — distinct hues, no hex input
  const CREW_PRESET_COLORS = [
    '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16',
    '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9',
    '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF',
    '#EC4899', '#F43F5E', '#78716C', '#6B7280', '#1E293B',
  ];

  const updateCrew = (id, fn) => setCrewAdds(prev => prev.map(c => (c.id === id ? fn(c) : c)));

  const handleAddCrew = () => {
    if (!newCrewName.trim()) return;
    const id = newTextId('crew', new Set([...storedIds, ...crewAdds.map(c => c.id)]));
    setCrewAdds(prev => [...prev, {
      id,
      name: newCrewName.trim(),
      description: newCrewDescription.trim(),
      color: newCrewColor,
      members: [],
      pending_invites: [],
      placeholders: [],
      specialties: [],
      active: true,
    }]);
    setNewCrewName('');
    setNewCrewDescription('');
    setNewCrewColor(CREW_PRESET_COLORS[(crewAdds.length + 1) % CREW_PRESET_COLORS.length]);
  };

  // IDI-657 D1: a person, never a login — with an email an invite, else a placeholder.
  const handleAddMember = (member) => {
    if (activeCrewId === null) return;
    const name = `${member.firstName || ''} ${member.lastName || ''}`.trim();
    const email = String(member.email || '').trim();
    updateCrew(activeCrewId, c => (email
      ? { ...c, pending_invites: [...c.pending_invites, { name, email }] }
      : { ...c, placeholders: [...c.placeholders, { name }] }));
  };

  const handleRemovePerson = (crewId, key, idx) =>
    updateCrew(crewId, c => ({ ...c, [key]: c[key].filter((_, i) => i !== idx) }));

  const handleSubmit = () => onNext({ crewAdds });

  return (
    <div>
      <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Set Up Crews</h2>
      <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
        Organize your field team into crews. Crews are assigned to work orders and shown on the calendar.
      </p>

      {/* Add Crew */}
      <div className="card p-4 mb-6">
        <div className="flex gap-3 mb-3">
          <Input
            placeholder="Crew name (e.g., Crew Alpha)"
            value={newCrewName}
            onChange={(e) => setNewCrewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddCrew()}
            className="flex-1"
          />
          <Button variant="secondary" onClick={handleAddCrew} disabled={!newCrewName.trim()}>
            <PlusIcon className="h-4 w-4 mr-1" /> Add Crew
          </Button>
        </div>
        <textarea
          placeholder="Description (optional) — e.g., Spray foam specialists, residential"
          value={newCrewDescription}
          onChange={(e) => setNewCrewDescription(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-[color:var(--color-border)] px-3 py-2 text-sm mb-3 resize-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
        />
        {/* Inline Crew Color Picker — 20 preset circles */}
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">Crew Color</label>
          <div className="grid grid-cols-10 gap-2">
            {CREW_PRESET_COLORS.map((color) => (
              <button
                key={color}
                onClick={() => setNewCrewColor(color)}
                className={`h-7 w-7 rounded-full transition-all ${
                  newCrewColor === color
                    ? 'ring-2 ring-offset-2 ring-red-500 scale-110'
                    : 'hover:scale-110'
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Crew List — Settings' crews read-only, then setup's adds */}
      {storedCrews.length + crewAdds.length > 0 ? (
        <div className="space-y-4 mb-4">
          {storedCrews.map((crew) => {
            const n = crewPeople(crew);
            return (
              <div key={`s-${crew.id}`} data-testid="stored-crew" className="card flex items-center gap-3 bg-gray-50 px-4 py-3">
                <div className="h-5 w-5 rounded-full border-2 border-white" style={{ backgroundColor: crew.color || '#6B7280' }} />
                <div>
                  <span className="text-sm font-medium text-gray-900">{crew.name}</span>
                  {crew.description && <p className="text-xs text-gray-500">{crew.description}</p>}
                </div>
                <span className="text-xs text-gray-500">{n} member{n !== 1 ? 's' : ''}</span>
              </div>
            );
          })}
          {crewAdds.map((crew) => {
            const n = crewPeople(crew);
            const people = [
              ...crew.pending_invites.map((p, i) => ({ ...p, key: 'pending_invites', i })),
              ...crew.placeholders.map((p, i) => ({ ...p, key: 'placeholders', i })),
            ];
            return (
              <div key={crew.id} className="card overflow-hidden">
                <div className="flex items-center justify-between bg-gray-50 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="relative group">
                      <button
                        onClick={() => setColorCrewId(colorCrewId === crew.id ? null : crew.id)}
                        className="h-5 w-5 rounded-full border-2 border-white"
                        style={{ backgroundColor: crew.color }}
                        title="Change color"
                      />
                      {colorCrewId === crew.id && (
                        <div className="absolute top-full left-0 mt-2 p-2 bg-white border border-[color:var(--color-border)] z-10 grid grid-cols-5 gap-1.5 w-[180px]" style={{ borderRadius: 'var(--radius-md)' }}>
                          {CREW_PRESET_COLORS.map((color) => (
                            <button
                              key={color}
                              onClick={() => { updateCrew(crew.id, c => ({ ...c, color })); setColorCrewId(null); }}
                              className={`h-6 w-6 rounded-full transition-all ${
                                crew.color === color ? 'ring-2 ring-offset-1 ring-red-500 scale-110' : 'hover:scale-110'
                              }`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900">{crew.name}</span>
                      {crew.description && <p className="text-xs text-gray-500">{crew.description}</p>}
                    </div>
                    <span className="text-xs text-gray-500">{n} member{n !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setActiveCrewId(crew.id); setShowAddMember(true); }}>
                      <PlusIcon className="h-3 w-3 mr-1" /> Add Member
                    </Button>
                    <button
                      aria-label={`Remove ${crew.name}`}
                      onClick={() => setCrewAdds(prev => prev.filter(c => c.id !== crew.id))}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {people.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {people.map((p) => (
                      <div key={`${p.key}-${p.i}`} className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-sm text-gray-900">
                          {p.name}{p.email ? <span className="text-xs text-gray-500"> ({p.email})</span> : null}
                        </span>
                        <button onClick={() => handleRemovePerson(crew.id, p.key, p.i)} className="text-gray-400 hover:text-red-500 transition-colors">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-4 text-center">
                    <p className="text-xs text-gray-400">No members yet — add people to this crew</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-md border-2 border-dashed border-[color:var(--color-border)] p-8 text-center mb-4">
          <UsersIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No crews created yet</p>
          <p className="text-xs text-gray-400 mt-1">Create crews to organize your field team</p>
        </div>
      )}

      <div className="mt-8 flex justify-between">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={onSkip}>Skip for now</Button>
          <Button onClick={handleSubmit} loading={saving} size="lg">
            Continue <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Modals */}
      <AddCrewMemberModal
        open={showAddMember}
        onClose={() => setShowAddMember(false)}
        onAdd={handleAddMember}
        existingMembers={crewAdds.flatMap(c => c.pending_invites)}
      />
    </div>
  );
}

// ─── Step 7: Resources ──────────────────────────────────────────────────────

const CUSTOM_CATEGORY = '__custom__';

// Exported for tests only (IDI-657).
function Step7Resources({ data, onNext, onBack, onSkip, saving }) {
  // IDI-657 D2/D4: Resource Types' categories and rows (read-only); setup's
  // adds are the step's only progress. A replayed add already stored shows once.
  const { items: storedRows } = useSettingsList('resource_types');
  const { categories } = useResourceCategories();
  const storedIds = new Set((storedRows || []).map(r => String(r.id)));
  const [resourceAdds, setResourceAdds] = useState(() => (Array.isArray(data.resourceAdds) ? data.resourceAdds : []));
  const shownAdds = resourceAdds.filter(r => !storedIds.has(String(r.id)));
  const [showExampleWO, setShowExampleWO] = useState(false);
  const blank = { name: '', category: 'equipment', customCategory: '', description: '' };
  const [newResource, setNewResource] = useState(blank);
  const [categoryError, setCategoryError] = useState('');

  const handleAddResource = () => {
    if (!newResource.name.trim()) return;
    const isCustom = newResource.category === CUSTOM_CATEGORY;
    const category = isCustom ? newResource.customCategory.trim() : newResource.category;
    if (!category) { setCategoryError('Category is required'); return; }
    const id = newTextId('res', new Set([...storedIds, ...resourceAdds.map(r => r.id)]));
    setResourceAdds(prev => [...prev, {
      id, name: newResource.name.trim(), category, description: newResource.description.trim(), active: true,
    }]);
    setNewResource(blank);
    setCategoryError('');
  };

  const handleSubmit = () => onNext({ resourceAdds: shownAdds });

  const all = [...(storedRows || []).map(r => ({ ...r, stored: true })), ...shownAdds];
  const labelOf = (key) => categories.find(c => c.key === key)?.label || key;
  const groupKeys = [...new Set([...categories.map(c => c.key), ...all.map(r => r.category)])];
  const groupedResources = groupKeys.map(key => ({
    value: key, label: labelOf(key), items: all.filter(r => r.category === key),
  })).filter(g => g.items.length > 0);

  return (
    <div>
      <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Resources & Equipment</h2>
      <p className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>
        Add your rigs, trailers, vehicles, and equipment. These can be assigned to work orders for scheduling.
      </p>
      <button
        onClick={() => setShowExampleWO(true)}
        className="text-sm text-red-600 hover:text-red-700 font-medium mb-8 inline-block"
      >
        See an example work order →
      </button>

      {/* Add Resource Form */}
      <div className="card p-4 mb-6">
        <p className="text-sm font-medium text-gray-900 mb-3">Add a resource</p>
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-4">
            <Input
              placeholder="Name (e.g., Truck #1)"
              value={newResource.name}
              onChange={(e) => setNewResource(prev => ({ ...prev, name: e.target.value }))}
              size="sm"
            />
          </div>
          <div className="col-span-3">
            <Select
              aria-label="Category"
              value={newResource.category}
              onChange={(e) => { const v = e.target.value; setNewResource(prev => ({ ...prev, category: v })); setCategoryError(''); }}
            >
              {categories.map(opt => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
              <option value={CUSTOM_CATEGORY}>+ Custom category…</option>
            </Select>
          </div>
          <div className="col-span-3">
            <Input
              placeholder="Description (optional)"
              value={newResource.description}
              onChange={(e) => setNewResource(prev => ({ ...prev, description: e.target.value }))}
              size="sm"
            />
          </div>
          <div className="col-span-2 flex items-start">
            <Button
              variant="secondary"
              size="sm"
              aria-label="Add resource"
              onClick={handleAddResource}
              disabled={!newResource.name.trim()}
              className="w-full"
            >
              <PlusIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {newResource.category === CUSTOM_CATEGORY && (
          <div className="mt-3">
            <Input
              label="New category name"
              value={newResource.customCategory}
              onChange={(e) => { const v = e.target.value; setNewResource(prev => ({ ...prev, customCategory: v })); setCategoryError(''); }}
              placeholder="e.g., Spray Rigs"
              error={categoryError}
              size="sm"
            />
          </div>
        )}
        {categoryError && newResource.category !== CUSTOM_CATEGORY && (
          <p className="mt-1 text-xs text-red-600">{categoryError}</p>
        )}
      </div>

      {/* Resource List — Resource Types' rows read-only, then setup's adds */}
      {groupedResources.length > 0 ? (
        <div className="space-y-4 mb-4">
          {groupedResources.map((group) => (
            <div key={group.value}>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                {group.label} ({group.items.length})
              </p>
              <div className="card divide-y divide-[color:var(--color-border-subtle)]">
                {group.items.map((resource) => (
                  <div key={`${resource.stored ? 's' : 'a'}-${resource.id}`} className="flex items-center justify-between px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <WrenchScrewdriverIcon className="h-4 w-4 text-gray-400" />
                      <div>
                        <span className="text-sm text-gray-900">{resource.name}</span>
                        {resource.description && (
                          <p className="text-xs text-gray-500">{resource.description}</p>
                        )}
                      </div>
                    </div>
                    {!resource.stored && (
                      <button
                        aria-label={`Remove ${resource.name}`}
                        onClick={() => setResourceAdds(prev => prev.filter(r => r.id !== resource.id))}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md border-2 border-dashed border-[color:var(--color-border)] p-8 text-center mb-4">
          <WrenchScrewdriverIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No resources added yet</p>
          <p className="text-xs text-gray-400 mt-1">Add your trucks, trailers, and equipment</p>
        </div>
      )}

      <div className="mt-8 flex justify-between">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={onSkip}>Skip for now</Button>
          <Button onClick={handleSubmit} loading={saving} size="lg">
            Continue <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Example Work Order Modal */}
      <ExampleWorkOrderModal
        open={showExampleWO}
        onClose={() => setShowExampleWO(false)}
      />
    </div>
  );
}

// ─── Step 8: Add Customers ──────────────────────────────────────────────────

// IDI-667 D1: header, then every row; quoted commas, doubled quotes, CRLF,
// BOM; blank rows skipped. Each row keeps its spreadsheet row number (header 1).
function parseCSV(text) {
  const src = String(text || '').replace(/^\uFEFF/, '');
  const lines = [];
  let row = [], cell = '', quoted = false, line = 1, start = 1;
  const endRow = () => { row.push(cell); lines.push({ r: start, cells: row.map(c => c.trim()) }); row = []; cell = ''; };
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cell += ch; // IDI-749 D1: a quoted line break stays in this row
    } else if (ch === '"') quoted = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\r' && src[i + 1] === '\n') { /* CRLF: the \n ends the row */ }
    else if (ch === '\n' || ch === '\r') { endRow(); line++; start = line; }
    else cell += ch;
  }
  if (cell !== '' || row.length) endRow();
  const kept = lines.filter(l => l.cells.some(c => c !== ''));
  const [head, ...rows] = kept;
  return { headers: head ? head.cells : [], rows };
}

const digitsOf = (v) => String(v || '').replace(/\D/g, '');
const lcs = (v) => String(v || '').trim().toLowerCase();

// IDI-658 D1's body, shared by Add Customer and the CSV import (D2).
function buildCustomerBody(f, type) {
  const firstName = (f.firstName || '').trim();
  const lastName = (f.lastName || '').trim();
  const company = (f.companyName || '').trim();
  const phone = (f.phone || '').trim();
  const email = (f.email || '').trim();
  const personName = `${firstName} ${lastName}`.trim();
  const name = personName || company;
  return {
    entityType: 'customer',
    name,
    first_name: firstName,
    last_name: lastName,
    company,
    use_company_as_display: !personName,
    customer_type: type,
    status: 'active',
    phone,
    email,
    // IDI-745 D1: the contact's own name, blank for a company-only customer,
    // as Customers → Add Customer leaves it.
    contacts: [{ first_name: firstName, last_name: lastName, name: personName, phone, email, is_primary: true }],
  };
}

// Same checks, order and wording as AddCustomerDrawer's validate().
function customerError(f) {
  const personName = `${f.firstName || ''} ${f.lastName || ''}`.trim();
  if (!personName && !(f.companyName || '').trim()) return 'Customer name is required';
  if (!(f.phone || '').trim() && !(f.email || '').trim()) return 'Primary contact phone or email is required';
  return '';
}

const rowsWord = (n) => `row${n === 1 ? '' : 's'}`;

function Step8Customers({ data, onNext, onBack, onSkip, onSaveProgress, saving }) {
  // IDI-658 D1/D3: each "Add Customer" creates through POST /v1/customers;
  // setup keeps only its own {id, name} list (a pre-change run's `customers`
  // is dropped on load by dropUntouchedMargins).
  const { getOptions } = useSettingsContext();
  const [addedTypes, setAddedTypes] = useState([]);
  const settingsTypes = getOptions('customer_types');
  // A just-added type shows until Settings' list re-reads with it.
  const typeOptions = [...settingsTypes, ...addedTypes.filter(a => !settingsTypes.some(o => o.value === a.value))];
  const [customers, setCustomers] = useState(data.setupCustomers || []);
  const [showCSVMapping, setShowCSVMapping] = useState(false);
  const [csvData, setCsvData] = useState({ headers: [], rows: [], fileName: '', key: 0 });
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ n: 0, N: 0 });
  const [importResult, setImportResult] = useState(null);
  const blankForm = { firstName: '', lastName: '', companyName: '', email: '', phone: '' };
  const [manualForm, setManualForm] = useState({ ...blankForm, type: '' });
  const [formError, setFormError] = useState('');
  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);
  const [showTypeAdd, setShowTypeAdd] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [typeError, setTypeError] = useState('');
  const [addingType, setAddingType] = useState(false);
  const fileInputRef = useRef(null);

  const handleCSVUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const { headers, rows } = parseCSV(event.target.result);
      if (rows.length === 0) {
        setImportResult({ empty: true });
        return;
      }
      setImportResult(null);
      setCsvData(prev => ({ headers, rows, fileName: file.name, key: prev.key + 1 }));
      setShowCSVMapping(true);
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  // IDI-667 D2-D5: unknown types first (serially, once each), then one create
  // per row in file order, then one progress save of what was created.
  const runImport = async (mapping, untypedPick) => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    setImporting(true);
    const rows = csvData.rows;
    setProgress({ n: 0, N: rows.length }); // IDI-749 D2: this file's count from the start
    const fieldsOf = (cells) => {
      const f = {};
      Object.entries(mapping).forEach(([idx, field]) => { if (field) f[field] = cells[Number(idx)] || ''; });
      return f;
    };
    const parsed = rows.map(row => ({ r: row.r, f: fieldsOf(row.cells) }));
    const resolved = {}; // lc(name) → value, or null when its add failed
    const added = [];
    for (const { f } of parsed) {
      const t = (f.type || '').trim();
      if (!t || lcs(t) in resolved) continue;
      const match = typeOptions.find(o => lcs(o.label) === lcs(t));
      if (match) { resolved[lcs(t)] = match.value; continue; }
      try {
        await settingsAPI.addListItem('customer_types', { name: t });
        const value = t.toLowerCase().replace(/\s+/g, '_');
        resolved[lcs(t)] = value;
        added.push({ value, label: t });
      } catch (err) {
        resolved[lcs(t)] = null;
      }
    }
    if (added.length) setAddedTypes(prev => [...prev, ...added]);

    const created = [];
    const createdKeys = [];
    const skipped = [];
    const listNames = new Set(customers.map(c => lcs(c.name)));
    for (let i = 0; i < parsed.length; i++) {
      const { r, f } = parsed[i];
      setProgress({ n: i + 1, N: parsed.length });
      const t = (f.type || '').trim();
      const body = buildCustomerBody(f, t ? resolved[lcs(t)] : untypedPick);
      const phone = digitsOf(body.phone);
      const email = lcs(body.email);
      let reason = customerError(f);
      if (!reason && t && resolved[lcs(t)] === null) reason = "Couldn't add this customer type. Try again.";
      if (!reason && (listNames.has(lcs(body.name)) || createdKeys.some(k => k.name === lcs(body.name)
        && ((phone && k.phone === phone) || (email && k.email === email))))) reason = 'Already added';
      if (!reason) {
        const addr = toServiceAddressShape({ street: f.street || '', city: f.city || '', state: f.state || '', zip: f.zip || '' });
        if (addr) body.serviceAddress = addr;
        try {
          const res = await customerAPI.createCustomer(body);
          created.push({ id: res?.id ?? res?.data?.id ?? res?.customer?.id, name: body.name });
          createdKeys.push({ name: lcs(body.name), phone, email });
        } catch (err) {
          reason = "Couldn't add this customer.";
        }
      }
      if (reason) skipped.push({ r, reason });
    }

    setImportResult({ added: created.length, total: parsed.length, skipped });
    setShowCSVMapping(false);
    setImporting(false);
    if (created.length) {
      const next = [...customers, ...created];
      setCustomers(next);
      try {
        if (onSaveProgress) await onSaveProgress({ setupCustomers: next });
      } finally {
        creatingRef.current = false;
      }
    } else {
      creatingRef.current = false;
    }
  };

  const setField = (k, v) => setManualForm(prev => ({ ...prev, [k]: v }));

  const validate = () => customerError(manualForm) || (!manualForm.type ? 'Customer type is required' : '');

  const handleManualAdd = async () => {
    if (creatingRef.current) return;
    const err = validate();
    setFormError(err);
    if (err) return;
    creatingRef.current = true;
    setCreating(true);
    const f = manualForm;
    const payload = buildCustomerBody(f, f.type);
    const { name } = payload;
    try {
      const res = await customerAPI.createCustomer(payload);
      const id = res?.id ?? res?.data?.id ?? res?.customer?.id;
      const next = [...customers, { id, name }];
      setCustomers(next);
      setManualForm({ ...blankForm, type: f.type });
      if (onSaveProgress) await onSaveProgress({ setupCustomers: next });
    } catch (e) {
      setFormError("Couldn't add this customer. Try again.");
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  // IDI-658 D2: pick a matching type (any case), else add it to Settings'
  // Customer Types and select the value getOptions derives for it.
  const handleAddType = async () => {
    const typed = newTypeName.trim();
    if (!typed || addingType) return;
    setTypeError('');
    const match = typeOptions.find(o => String(o.label || '').trim().toLowerCase() === typed.toLowerCase());
    if (match) {
      setField('type', match.value);
      setNewTypeName('');
      setShowTypeAdd(false);
      return;
    }
    setAddingType(true);
    try {
      await settingsAPI.addListItem('customer_types', { name: typed });
      const value = typed.toLowerCase().replace(/\s+/g, '_');
      setAddedTypes(prev => [...prev, { value, label: typed }]);
      setField('type', value);
      setNewTypeName('');
      setShowTypeAdd(false);
    } catch (e) {
      setTypeError("Couldn't add this customer type. Try again.");
    } finally {
      setAddingType(false);
    }
  };

  const handleSubmit = () => onNext({ setupCustomers: customers });

  return (
    <div>
      <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Add Your Customers</h2>
      <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
        Import your existing customer list or add them manually. You can always add more later.
      </p>

      <div className="card p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-gray-900">Add a customer</p>
          {customers.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}>
              <CloudArrowUpIcon className="h-4 w-4 mr-1" /> Import CSV
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Input placeholder="First name" value={manualForm.firstName}
            onChange={(e) => setField('firstName', e.target.value)} size="sm" />
          <Input placeholder="Last name" value={manualForm.lastName}
            onChange={(e) => setField('lastName', e.target.value)} size="sm" />
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Input placeholder="Customer name" value={manualForm.companyName}
            onChange={(e) => setField('companyName', e.target.value)} size="sm" />
          <div>
            <Select aria-label="Customer type" value={manualForm.type}
              onChange={(e) => setField('type', e.target.value)}>
              <option value="">Select type...</option>
              {typeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
            {!showTypeAdd ? (
              <button type="button" onClick={() => setShowTypeAdd(true)}
                className="mt-1 text-xs text-gray-600 hover:text-red-600 underline">
                + Add customer type
              </button>
            ) : (
              <div className="mt-2 flex gap-2">
                <Input placeholder="Customer type name" value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)} size="sm" />
                <Button variant="secondary" size="sm" onClick={handleAddType} loading={addingType}>Add</Button>
              </div>
            )}
            {typeError && <p className="mt-1 text-xs" style={{ color: 'var(--color-status-error-text)' }}>{typeError}</p>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Input placeholder="Email" type="email" value={manualForm.email}
            onChange={(e) => setField('email', e.target.value)} size="sm" />
          <Input placeholder="Phone" type="tel" value={manualForm.phone}
            onChange={(e) => setField('phone', formatPhoneInput(e.target.value))} size="sm" />
        </div>
        {formError && <p className="mb-2 text-xs" style={{ color: 'var(--color-status-error-text)' }}>{formError}</p>}
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" onClick={handleManualAdd} disabled={creating} loading={creating}>
            <PlusIcon className="h-4 w-4 mr-1" /> Add Customer
          </Button>
        </div>
      </div>

      {/* CSV import is TERTIARY */}
      {customers.length === 0 && (
        <div className="text-center mb-6">
          <button onClick={() => fileInputRef.current?.click()}
            className="text-sm text-gray-500 hover:text-red-600 underline">
            Or import from a CSV file
          </button>
        </div>
      )}

      {importResult && (
        <div className="mb-4 text-sm text-gray-700" data-testid="csv-import-result">
          {importResult.empty ? (
            <p>No customer rows in this file.</p>
          ) : (
            <>
              <p>{`Imported ${importResult.added} of ${importResult.total} ${rowsWord(importResult.total)}.`}</p>
              {importResult.skipped.length > 0 && (
                <>
                  <p className="mt-1">{`${importResult.skipped.length} ${rowsWord(importResult.skipped.length)} skipped:`}</p>
                  <ul className="mt-1 text-xs text-gray-600">
                    {importResult.skipped.map(sk => <li key={sk.r}>{`Row ${sk.r}: ${sk.reason}`}</li>)}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Customer List — setup's own creates, names only */}
      {customers.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">
            {customers.length} customer{customers.length !== 1 ? 's' : ''} added
          </p>
          <div className="card max-h-60 overflow-y-auto divide-y divide-[color:var(--color-border-subtle)]">
            {customers.map((customer) => (
              <div key={customer.id || customer.name} className="px-4 py-2.5">
                <p className="text-sm text-gray-900">{customer.name}</p>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500">Edit or remove customers on the Customers page.</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.txt"
        onChange={handleCSVUpload}
        className="hidden"
      />

      <div className="mt-8 flex justify-between">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={onSkip}>Skip for now</Button>
          <Button onClick={handleSubmit} loading={saving} size="lg">
            Continue <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* CSV Mapping Modal */}
      <CSVMappingModal
        key={csvData.key}
        open={showCSVMapping}
        onClose={() => { if (!importing) setShowCSVMapping(false); }}
        onConfirm={runImport}
        csvHeaders={csvData.headers}
        rows={csvData.rows.map(row => row.cells)}
        typeOptions={typeOptions}
        importing={importing}
        progress={progress}
        fileName={csvData.fileName}
      />
    </div>
  );
}

// ─── Step 9: You're Set Up ──────────────────────────────────────────────────

function Step9Complete({ data, skippedSteps, onComplete, saving }) {
  const hasSkipped = skippedSteps.size > 0;

  const completedItems = [
    { label: 'Profile', done: !!(data.firstName && data.lastName) },
    { label: 'Business info', done: !!data.companyName },
    { label: 'Margins & labor', done: !skippedSteps.has(3) },
    { label: 'Tax & defaults', done: !skippedSteps.has(4) },
    { label: 'Team invitations', done: data.invitees?.length > 0 },
    { label: 'Crews', done: (data.storedCrews?.length || 0) + (data.crewAdds?.length || 0) > 0 },
    { label: 'Resources', done: data.resourceAdds?.length > 0 },
    { label: 'Customers', done: data.setupCustomers?.length > 0 },
  ];

  const completedCount = completedItems.filter(i => i.done).length;

  return (
    <div className="text-center py-8">
      {/* Success Icon */}
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
        <CheckCircleIcon className="h-12 w-12 text-green-600" />
      </div>

      <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
        {hasSkipped ? "You're Almost There!" : "You're All Set!"}
      </h2>
      <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
        {hasSkipped
          ? "You've completed the basics. You can finish the skipped steps anytime from Settings."
          : "Your account is fully configured and ready to go. Start creating quotes and managing your business!"}
      </p>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto mb-8 text-left">
        {completedItems.map((item, idx) => (
          <div
            key={idx}
            className={`flex items-center gap-2 rounded-md border border-[color:var(--color-border)] px-3 py-2 ${
              item.done
                ? 'border-green-200 bg-green-50'
                : 'border-[color:var(--color-border)] bg-gray-50'
            }`}
          >
            {item.done ? (
              <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
            ) : (
              <div className="h-4 w-4 rounded-full border-2 border-[color:var(--color-border)] flex-shrink-0" />
            )}
            <span className={`text-sm ${item.done ? 'text-green-800' : 'text-gray-500'}`}>
              {item.label}
            </span>
          </div>
        ))}
      </div>

      {/* Progress */}
      <div className="max-w-sm mx-auto mb-8">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>Setup progress</span>
          <span>{completedCount}/{completedItems.length} complete</span>
        </div>
        <div className="h-2 rounded-full bg-gray-200">
          <div
            className="h-2 rounded-full bg-green-500 transition-all"
            style={{ width: `${(completedCount / completedItems.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Skipped Steps Warning */}
      {hasSkipped && (
        <div className="bg-amber-50 border border-amber-200 p-4 max-w-md mx-auto mb-8" style={{ borderRadius: 'var(--radius-md)' }}>
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-left">
              <p className="text-sm font-medium text-amber-800">Some steps were skipped</p>
              <p className="text-xs text-amber-700 mt-1">
                You can complete them anytime from Settings → Company Setup.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <Button onClick={onComplete} loading={saving} size="lg">
        Go to my dashboard <ArrowRightIcon className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}
